import React, { useState, useEffect, useRef } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import { Link } from "../components/L";
import axios from "axios";
import useSEO from "../hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const useScrollReveal = (threshold = 0.15) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.unobserve(el); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
};

const BlogCard = ({ post, index }) => {
  const [ref, visible] = useScrollReveal(0.1);
  return (
    <Link
      ref={ref}
      key={post.id}
      to={`/blog/${post.id}`}
      className="group overflow-hidden rounded-xl border border-slate-200 bg-white"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(40px)',
        transition: `opacity 0.6s ease ${index * 0.15}s, transform 0.6s ease ${index * 0.15}s, box-shadow 0.5s ease`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 20px 40px rgba(8,112,122,0.12)'; e.currentTarget.style.transform = 'translateY(-6px)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; e.currentTarget.style.transform = 'translateY(0)'; }}
      data-testid={`blog-card-${post.id}`}
    >
      <div className="h-52 overflow-hidden">
        <img
          src={post.cover_image || 'https://images.pexels.com/photos/23625648/pexels-photo-23625648.jpeg'}
          alt={post.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
      </div>
      <div className="p-6">
        <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#1CA3B0' }}>{post.category}</span>
        <h3 className="font-bold text-[#0A0D10] mt-2 mb-3 line-clamp-2" style={{ fontFamily: "'Noto Sans', sans-serif" }}>{post.title}</h3>
        <p className="text-sm text-[#4A5568] line-clamp-3">{post.excerpt}</p>
        <div className="flex items-center mt-4 pt-4 border-t border-slate-100 text-xs text-[#828D9E]">
          <span><i className="fas fa-calendar-alt mr-1"></i> {new Date(post.created_at).toLocaleDateString('sq-AL')}</span>
        </div>
      </div>
    </Link>
  );
};

const BlogPage = () => {
  const { tx } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [heroVisible, setHeroVisible] = useState(false);

  useSEO({
    title: tx("Blog - Artikuj dhe Lajme per Dentistrne"),
    description: tx("Lexoni artikujt me te fundit nga Albanian Aligners Academy - tema mbi aligners, implantologji, ortodonci moderne, estetike dentare dhe teknologji dixhitale."),
    keywords: "blog dentar, artikuj ortodoncie, lajme aligners, implantologji artikuj, dentistri estetike blog",
    canonical: "https://aligners.academy/blog",
  });

  useEffect(() => {
    axios.get(`${API}/blog`).then(r => setPosts(r.data.filter(p => p.published))).catch(() => {});
    setTimeout(() => setHeroVisible(true), 100);
  }, []);

  return (
    <div data-testid="blog-page">
      {/* Hero */}
      <section className="relative py-10 md:py-14 px-6 overflow-hidden" style={{ background: 'linear-gradient(130deg, #3CD4E1 0%, #003F72 100%)' }}>
        <div className="max-w-[1350px] mx-auto">
          <h1
            className="text-white mb-3"
            style={{
              fontFamily: "'Noto Sans', sans-serif", fontWeight: 700, fontSize: '48px', lineHeight: 1.2,
              opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.7s ease, transform 0.7s ease',
            }}
            data-testid="blog-title"
          >
            Blog
          </h1>
          <p
            className="text-white"
            style={{
              opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.7s ease 0.2s, transform 0.7s ease 0.2s',
            }}
          >
            {tx("Artikuj edukative ne stomatologji, aligners dhe ortodonci.")}
          </p>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 max-w-[1350px] mx-auto">
        {posts.length === 0 ? (
          <div className="text-center py-20">
            <i className="fas fa-blog text-5xl text-[#E2E8F0] mb-4"></i>
            <p className="text-[#828D9E] text-lg">{tx("Nuk ka artikuj.")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post, i) => (
              <BlogCard key={post.id} post={post} index={i} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default BlogPage;
